import { useCallback, useEffect } from "react";

import { EDITOR_LS_KEYS } from "@excalidraw/common";

import { EditorLocalStorage } from "../data/EditorLocalStorage";
import { useAtom, useAtomValue } from "../editor-jotai";
import { t } from "../i18n";
import {
  teleprompterStateAtom,
  teleprompterStatusAtom,
} from "../teleprompter/atoms";
import { useDraggableOverlay } from "../teleprompter/useDraggableOverlay";
import { useScrollAnimation } from "../teleprompter/useScrollAnimation";

import "./TeleprompterOverlay.scss";

const TeleprompterEditor = () => {
  const [state, setState] = useAtom(teleprompterStateAtom);
  const { settings } = state;

  const defaultX =
    typeof window !== "undefined" ? (window.innerWidth - 320) / 2 : 100;
  const defaultY =
    typeof window !== "undefined" ? (window.innerHeight - 500) / 2 : 100;

  const initialPos =
    state.position.x >= 0 ? state.position : { x: defaultX, y: defaultY };

  const { position, onDragPointerDown } = useDraggableOverlay(
    initialPos,
    state.size,
  );

  useEffect(() => {
    setState((prev) => ({ ...prev, position }));
  }, [position, setState]);

  useEffect(() => {
    EditorLocalStorage.set(EDITOR_LS_KEYS.TELEPROMPTER, settings);
  }, [settings]);

  const updateSetting = useCallback(
    <K extends keyof typeof settings>(key: K, value: typeof settings[K]) => {
      setState((prev) => ({
        ...prev,
        settings: { ...prev.settings, [key]: value },
      }));
    },
    [setState],
  );

  const handlePlay = useCallback(() => {
    // 立即切换到播放面板（paused 状态，不滚动）
    setState((prev) => ({
      ...prev,
      status: "paused",
      scrollOffset: 0,
    }));
    // 1 秒后自动开始播放
    setTimeout(() => {
      setState((prev) =>
        prev.status === "paused" && prev.scrollOffset === 0
          ? { ...prev, status: "playing" }
          : prev,
      );
    }, 1000);
  }, [setState]);

  const handleClose = useCallback(() => {
    setState((prev) => ({
      ...prev,
      status: "idle",
    }));
  }, [setState]);

  return (
    <div
      className="teleprompter-overlay"
      style={{ left: position.x, top: position.y }}
    >
      <div className="teleprompter-editor">
        <div
          className="teleprompter-editor__drag-handle"
          onPointerDown={onDragPointerDown}
        >
          <h3 className="teleprompter-editor__title">
            {t("teleprompter.settingsTitle")}
          </h3>
          <button
            className="teleprompter-editor__close-btn"
            onClick={handleClose}
            onPointerDown={(e) => e.stopPropagation()}
            type="button"
          >
            &times;
          </button>
        </div>

        <div className="teleprompter-editor__content">
          <textarea
            className="teleprompter-editor__textarea"
            placeholder={t("teleprompter.textPlaceholder")}
            value={settings.text}
            onChange={(e) => updateSetting("text", e.target.value)}
          />

          <div
            className="teleprompter-editor__preview"
            style={{
              background: `rgba(255, 255, 255, ${settings.panelOpacity})`,
              border: "1px solid var(--color-border)",
            }}
          >
            {settings.text.trim() ? (
              <div
                className="teleprompter-editor__preview-text"
                style={{
                  fontSize: settings.fontSize,
                  lineHeight: settings.lineHeight,
                }}
              >
                {settings.text}
              </div>
            ) : (
              <span className="teleprompter-editor__preview-placeholder">
                {t("teleprompter.previewPlaceholder")}
              </span>
            )}
          </div>

          <div className="teleprompter-editor__field">
            <label className="teleprompter-editor__label">
              <span>{t("teleprompter.fontSize")}</span>
              <span>{settings.fontSize}px</span>
            </label>
            <input
              className="teleprompter-editor__slider"
              type="range"
              min={16}
              max={72}
              step={1}
              value={settings.fontSize}
              onChange={(e) =>
                updateSetting("fontSize", Number(e.target.value))
              }
            />
          </div>

          <div className="teleprompter-editor__field">
            <label className="teleprompter-editor__label">
              <span>{t("teleprompter.lineHeight")}</span>
              <span>{settings.lineHeight.toFixed(1)}</span>
            </label>
            <input
              className="teleprompter-editor__slider"
              type="range"
              min={1.0}
              max={3.0}
              step={0.1}
              value={settings.lineHeight}
              onChange={(e) =>
                updateSetting("lineHeight", Number(e.target.value))
              }
            />
          </div>

          <div className="teleprompter-editor__field">
            <label className="teleprompter-editor__label">
              <span>{t("teleprompter.scrollSpeed")}</span>
              <span>{settings.scrollSpeed}px/s</span>
            </label>
            <input
              className="teleprompter-editor__slider"
              type="range"
              min={10}
              max={200}
              step={5}
              value={settings.scrollSpeed}
              onChange={(e) =>
                updateSetting("scrollSpeed", Number(e.target.value))
              }
            />
          </div>

          <div className="teleprompter-editor__field">
            <label className="teleprompter-editor__label">
              <span>{t("teleprompter.panelOpacity")}</span>
              <span>{(settings.panelOpacity * 100).toFixed(0)}%</span>
            </label>
            <input
              className="teleprompter-editor__slider"
              type="range"
              min={0.2}
              max={1.0}
              step={0.05}
              value={settings.panelOpacity}
              onChange={(e) =>
                updateSetting("panelOpacity", Number(e.target.value))
              }
            />
          </div>

          <button
            className="teleprompter-editor__play-btn"
            onClick={handlePlay}
            disabled={settings.text.trim().length === 0}
            type="button"
          >
            {t("teleprompter.play")}
          </button>
        </div>
      </div>
    </div>
  );
};

const TeleprompterPlayer = () => {
  const [state, setState] = useAtom(teleprompterStateAtom);
  const { settings, status } = state;

  const defaultX =
    typeof window !== "undefined"
      ? (window.innerWidth - state.size.width) / 2
      : 100;
  const defaultY =
    typeof window !== "undefined"
      ? (window.innerHeight - state.size.height) / 2
      : 100;

  const initialPos =
    state.position.x >= 0 ? state.position : { x: defaultX, y: defaultY };

  const { position, size, onDragPointerDown, onResizePointerDown } =
    useDraggableOverlay(initialPos, state.size);

  useEffect(() => {
    setState((prev) => ({ ...prev, position, size }));
  }, [position, size, setState]);

  const handleBack = useCallback(() => {
    setState((prev) => ({
      ...prev,
      status: "editing",
      scrollOffset: 0,
    }));
  }, [setState]);

  const handleClose = useCallback(() => {
    setState((prev) => ({
      ...prev,
      status: "idle",
    }));
  }, [setState]);

  const handlePause = useCallback(() => {
    setState((prev) => ({
      ...prev,
      status: "paused",
    }));
  }, [setState]);

  const handleResume = useCallback(() => {
    setState((prev) => ({
      ...prev,
      status: "playing",
    }));
  }, [setState]);

  const onReachEnd = useCallback(() => {
    handlePause();
  }, [handlePause]);

  const onOffsetChange = useCallback(
    (offset: number) => {
      setState((prev) => ({ ...prev, scrollOffset: offset }));
    },
    [setState],
  );

  const { textRef, containerRef, resetOffset } = useScrollAnimation({
    status,
    scrollSpeed: settings.scrollSpeed,
    initialOffset: state.scrollOffset,
    onReachEnd,
    onOffsetChange,
  });

  const handleStop = useCallback(() => {
    resetOffset(0);
    setState((prev) => ({
      ...prev,
      status: "paused",
      scrollOffset: 0,
    }));
  }, [resetOffset, setState]);

  return (
    <div
      className="teleprompter-overlay"
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        background: `rgba(255, 255, 255, ${settings.panelOpacity})`,
      }}
    >
      <div className="teleprompter-player">
        <div
          className="teleprompter-player__drag-handle"
          onPointerDown={onDragPointerDown}
        >
          <button
            className="teleprompter-player__close-btn"
            onClick={handleClose}
            onPointerDown={(e) => e.stopPropagation()}
            type="button"
          >
            &times;
          </button>
        </div>

        <div className="teleprompter-player__content" ref={containerRef}>
          <div
            className="teleprompter-player__text"
            ref={textRef}
            style={{
              fontSize: settings.fontSize,
              lineHeight: settings.lineHeight,
            }}
          >
            {settings.text}
          </div>
        </div>

        <div className="teleprompter-player__controls">
          {status === "playing" && (
            <button
              className="teleprompter-player__btn teleprompter-player__btn--pause"
              onClick={handlePause}
              type="button"
            >
              {t("teleprompter.pause")}
            </button>
          )}
          {status === "paused" && state.scrollOffset > 0 && (
            <button
              className="teleprompter-player__btn teleprompter-player__btn--resume"
              onClick={handleResume}
              type="button"
            >
              {t("teleprompter.resume")}
            </button>
          )}
          {status === "paused" && state.scrollOffset === 0 ? (
            <button
              className="teleprompter-player__btn teleprompter-player__btn--start"
              onClick={handleResume}
              type="button"
            >
              {t("teleprompter.start")}
            </button>
          ) : (
            <button
              className="teleprompter-player__btn teleprompter-player__btn--stop"
              onClick={handleStop}
              type="button"
            >
              {t("teleprompter.stop")}
            </button>
          )}
          <button
            className="teleprompter-player__btn teleprompter-player__btn--back"
            onClick={handleBack}
            type="button"
          >
            {t("teleprompter.back")}
          </button>
        </div>

        <div
          className="teleprompter-player__resize-handle"
          onPointerDown={onResizePointerDown}
        />
      </div>
    </div>
  );
};

export const TeleprompterOverlay = () => {
  const status = useAtomValue(teleprompterStatusAtom);

  if (status === "idle") {
    return null;
  }

  if (status === "editing") {
    return <TeleprompterEditor />;
  }

  return <TeleprompterPlayer />;
};
